import React, { useEffect } from "react";
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";

import { Api } from "@api/index";
import { connectToApi, ApiConnectedOwnProps } from "@api/components/connectToApi";
import { bundleNotFound, HasBundle } from "@app/components/notFoundHocs";
import { GlobalState } from "@app/models/state";
import { messages } from "@app/messages";
import { setTitle } from "@app/util";
import { Page } from "@ui/Page";
import { BundleView } from "@upload/components/BundleView";
import { messages as uMessages } from "@upload/messages";
import { makeGetBundle, PropsWithId } from "@upload/selectors";
import { compose } from "@util/compose";

type BundlesViewProps = HasBundle & ApiConnectedOwnProps;

const BundlesView = ({ bundle }: BundlesViewProps) => {
    const intl = useIntl();
    useEffect(() => {
        if (bundle.attrs != null) {
            setTitle(intl, messages.pageBundlesTitle, bundle.attrs.title);
        }
        else {
            setTitle(intl, messages.pageBundlesTitle);
        }
    }, [ bundle ]);

    const breadcrumbItems = [
        {
            text: <FormattedMessage {...messages.pageBundlesTitle} />,
            to: "/bundles",
        },
        {
            text: bundle.attrs != null
                ? bundle.attrs.title
                : <FormattedMessage {...uMessages.bundleDefaultTitle} values={{id: bundle.id}} />,
        },
    ];

    return (
        <Page style="blank" className="bundles-view-page" breadcrumbs={breadcrumbItems}>
            <BundleView bundle={bundle} />
        </Page>
    );
};

// Map the upload state to the component.
const makeMapStateToProps = () => {
    const getBundle = makeGetBundle();
    return (state: GlobalState, ownProps: PropsWithId) => {
        const bundle = getBundle(state, ownProps);
        const lastLoaded = [ bundle ? bundle.lastLoaded : null ];
        // Force reloads when the status name changes.
        const requestId = bundle != null && bundle.statusName != null ? [ bundle.statusName ] : null;
        return {
            bundle,
            lastLoaded,
            requestId,
        } as BundlesViewProps;
    };
};

const makeBundlesShow = (id: number) => () => {
    return Api.bundlesShow(id);
};

const ConnectedBundlesView = compose<BundlesViewProps, {}>(
    connect(makeMapStateToProps, null),
    connectToApi((props: PropsWithId) => {
        return {
            actionCreators: [ makeBundlesShow(Number(props.params.id)) ],
            showRefresh: false,
            cardPage: true,
        };
    }),
    bundleNotFound,
)(BundlesView);

export {
    BundlesView as BundlesViewPresenter,
    ConnectedBundlesView as BundlesView,
};
